import { create } from 'zustand';
import api from '../lib/api';
import type { CockpitResponse, CockpitScope, CockpitPeriod } from '../types';

/** Compute [from,to] ISO timestamps for a period, anchored in the browser's local tz. */
export function periodRange(period: CockpitPeriod, now: Date): { from: string; to: string } {
  const from = new Date(now);
  if (period === 'today') {
    from.setHours(0, 0, 0, 0);
  } else if (period === 'week') {
    const mondayOffset = (from.getDay() + 6) % 7; // 0 = Monday
    from.setDate(from.getDate() - mondayOffset);
    from.setHours(0, 0, 0, 0);
  } else if (period === 'quarter') {
    const quarterStartMonth = Math.floor(from.getMonth() / 3) * 3;
    from.setMonth(quarterStartMonth, 1);
    from.setHours(0, 0, 0, 0);
  } else if (period === 'year') {
    // School year (Jul 1 → Jun 30), anchored at Jul 1 **00:00 UTC** to match the
    // backend's `schoolYearStart`, which is UTC.
    //
    // CRM-121: this used to anchor to local midnight while the fundraising money
    // was cut at 00:00Z — so one screen carried two "school year" starts 7 hours
    // apart (Pacific), and the activity numbers silently covered a different
    // window than the dollars beside them. The sibling periods above stay local
    // on purpose: "today" and "this week" mean the viewer's day and week.
    const syStartYear = from.getUTCMonth() >= 6 ? from.getUTCFullYear() : from.getUTCFullYear() - 1;
    return { from: new Date(Date.UTC(syStartYear, 6, 1)).toISOString(), to: now.toISOString() };
  } else {
    from.setDate(1);
    from.setHours(0, 0, 0, 0);
  }
  return { from: from.toISOString(), to: now.toISOString() };
}

interface CockpitState {
  data: CockpitResponse | null;
  period: CockpitPeriod;
  isLoading: boolean;
  error: string | null;
  setPeriod: (p: CockpitPeriod) => void;
  fetch: (scope: CockpitScope, period: CockpitPeriod, teamId?: string | null) => Promise<void>;
  reset: () => void;
}

export const useCockpitStore = create<CockpitState>((set) => ({
  data: null,
  period: 'month',
  isLoading: false,
  error: null,
  setPeriod: (period) => set({ period }),
  fetch: async (scope, period, teamId) => {
    set({ isLoading: true, error: null, period });
    try {
      const { from, to } = periodRange(period, new Date());
      const params: Record<string, string> = { scope, from, to };
      if (scope === 'team' && teamId) params.teamId = teamId;
      const res = await api.get('/api/dashboard/cockpit', { params });
      set({ data: res.data as CockpitResponse, isLoading: false });
    } catch (error: any) {
      set({ isLoading: false, error: error.response?.data?.message || 'Failed to load dashboard' });
    }
  },
  reset: () => set({ data: null, period: 'month', isLoading: false, error: null }),
}));
